import React, { useEffect, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'

import { useTheme } from 'styled-components/native'

import { CONVERSATION_TYPE } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'
import { MessageToken } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Tokens'
import { InsertIntoTextIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/InsertIntoTextIcon'
import { SecondaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/secondaryButton/SecondaryButton'
import { MessengerUtilityItemList } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/messengerUtilityItemList/MessengerUtilityItemList'
import { decodeHtmlEntities, tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { mixpanelEvents } from '../../../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../../../mixpanel/mixpanelTracking'
import { getAuthUserId } from '../../../../../data/auth/authSelectors'
import { getSelectedClientId } from '../../../../../data/lyraTherapy/clientSelectors'
import { useAppDispatch } from '../../../../../data/store'
import { useClientAppointmentsData } from '../../../../clients/clientDetails/data/hooks/useClientAppointmentsData'
import { useSelectedClientData } from '../../../../clients/clientDetails/data/hooks/useSelectedClientData'
import { setToastContent } from '../../../../data/ltToastAutoActions'
import { InsertMessageOptions } from '../../../BaseChat'
import { useGetTemplatesQuery } from '../../../data/messagesApi'
import { getInsertMessageTemplateForClientId } from '../../../data/messagesSelectors'
import { setInsertMessageTemplateForClientId } from '../../../data/messagesSlice'
import { getAiConversationMixpanelProperties } from '../../../data/mixpanelUtils'
import { convertTemplateWithTokens, hasErrorSpanPattern } from '../../../data/utils'

type MessengerTemplatesSideBarProps = {
  insertMessage: (message: string, opts?: InsertMessageOptions) => void
  insertDirectBookingLink: () => void
  conversationType?: CONVERSATION_TYPE
}

const MessengerTemplatesSideBar: React.FC<MessengerTemplatesSideBarProps> = ({
  insertMessage,
  insertDirectBookingLink,
  conversationType,
}) => {
  const { colors } = useTheme()
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
  const providerId = useSelector(getAuthUserId)
  const selectedClientId = useSelector(getSelectedClientId)
  const { data: appointmentsData } = useClientAppointmentsData()
  const nextClientAppointment = appointmentsData?.find(
    (appt: any) =>
      new Date(`${appt.startDate} ${appt?.startTime}`) > new Date() && appt?.appointmentStatus !== 'canceled',
  )
  const { data: clientDetails } = useSelectedClientData()
  const pasteTemplateForClientId: string | null = useSelector(getInsertMessageTemplateForClientId)
  const dispatch = useAppDispatch()
  const { data: templates } = useGetTemplatesQuery(
    { providerId },
    {
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  )

  useEffect(
    () => () => {
      dispatch(setInsertMessageTemplateForClientId(null))
    },
    [dispatch],
  )

  const bodyText = useMemo(
    () => templates?.find((template) => template.id === selectedTemplateId)?.body,
    [selectedTemplateId, templates],
  )

  useEffect(() => {
    if (pasteTemplateForClientId === selectedClientId) {
      if (!bodyText) {
        return
      }
      const convertedTemplate = convertTemplateWithTokens(bodyText, clientDetails, nextClientAppointment)

      // Check if the template contains the booking link token and call insertDirectBookingLink if it does
      if (
        bodyText.includes(`{{token:${MessageToken.BOOKING_LINK}}}`) ||
        bodyText.includes('<token name="booking_link" label="Booking Link">')
      ) {
        insertDirectBookingLink()
      }

      if (hasErrorSpanPattern(convertedTemplate)) {
        dispatch(
          setToastContent({
            text: 'Some information in your message could not be loaded. Please correct any fields labelled with "ERROR."',
            id: 'MessagesPopover-Toast-Error',
            toastType: 'error',
          }),
        )
      } else {
        dispatch(
          setToastContent({
            text: 'Pasted into message',
            id: 'MessagesPopover-Toast-Success',
            toastType: 'success',
          }),
        )
      }
      dispatch(setInsertMessageTemplateForClientId(null))
      insertMessage(convertedTemplate, {
        attributesToAttach: {
          templates_submission_tracker_id: selectedTemplateId,
          message_template_id: selectedTemplateId,
        },
      })
    }
  }, [
    bodyText,
    clientDetails,
    nextClientAppointment,
    dispatch,
    insertDirectBookingLink,
    insertMessage,
    pasteTemplateForClientId,
    selectedClientId,
    selectedTemplateId,
  ])

  const sortedTemplates = templates ? [...templates] : []
  sortedTemplates.sort((a, b) => {
    const bDate = new Date(b.create_date)
    const aDate = new Date(a.create_date)
    return bDate.getTime() - aDate.getTime()
  })
  return (
    <MessengerUtilityItemList
      items={
        sortedTemplates?.map((template: any) => {
          return {
            id: template.id,
            header: template.name,
            text: decodeHtmlEntities(template.body),
            onPress: () => {},
          }
        }) ?? []
      }
      useTokenTextInput
      onItemExpand={(templateId) => setSelectedTemplateId(templateId)}
      expandedContent={() => {
        return (
          <>
            <SecondaryButton
              leftIcon={<InsertIntoTextIcon color={colors.linkDefault} />}
              testID={tID('MessengerTemplatesSideBar-insertIntoMessageButton')}
              fullWidth
              text={<FormattedMessage defaultMessage='Insert into message' description='Button to insert template' />}
              onPress={() => {
                track({
                  event: mixpanelEvents.INSERT_MESSAGE_TEMPLATE_INTO_MESSAGE_FROM_SIDEBAR,
                  properties: getAiConversationMixpanelProperties(conversationType),
                  details: {
                    templateId: selectedTemplateId,
                  },
                })
                dispatch(setInsertMessageTemplateForClientId(selectedClientId))
              }}
            />
          </>
        )
      }}
    />
  )
}

export default MessengerTemplatesSideBar
